// src/tenantAdmin/hooks/useManagedCompany.js
//
// The company the company tabs act on. Options = customers where the user is ADMIN
// (never viewer entries). Default = the company owning the active workspace when the
// user administers it (GET /credits/balance → customer_id), else the first option.
//
// ⛔ Why the default costs a network read at all: `TenantInfo` carries NO
// `customer_id`, so the claims alone cannot say which company owns the active
// workspace. `/credits/balance` is the only place that answer exists, and it works
// for any workspace the caller holds a claim on.
// ⛔ The `isCustomerAdminOf` check on the answer is not a nicety: the active
// workspace may belong to a company the user only VIEWS (or to none they claim),
// and calling /admin/customers/{cid}* for it would answer 403
// CUSTOMER_ADMIN_REQUIRED — which raises the global permissions-drift banner and
// tells the user their rights changed when in fact we picked the wrong company.
//
// ⭐ `activeCompanyId` — the company that owns the ACTIVE workspace, whatever the
// number of claims. It is what decides whether a link to /billing may be offered:
// /billing acts on the active workspace's company, so offering it while the tabs
// show ANOTHER company would open the wrong company's plans. null = unknown (not
// resolved yet, the read failed, or no company) → callers withhold the link.
//
// ⭐ `activeCompanyResolved` — whether that answer is KNOWN yet. `activeCompanyId`
// is null both while the read is in flight and on a real mismatch, and a consumer
// that cannot tell the two apart (the Crediti/Piani tab guard) would flash "this
// is another company's workspace" at every mount. It flips true when the read for
// the CURRENT active workspace has settled, success or failure.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { fetchCreditBalance } from '../../services/api/creditsApi.js';

export default function useManagedCompany() {
  const { customers, activeTenant, isCustomerAdminOf } = useAuth();
  const options = useMemo(() => (customers || []).filter(c => c.role === 'admin'), [customers]);
  const [companyId, setCompanyId] = useState(options.length === 1 ? options[0].customer_id : null);
  // The active workspace's company as /balance reports it, tagged with the
  // workspace it was read for so a stale answer is never read as the current one.
  const [activeRead, setActiveRead] = useState({ tenantId: null, customerId: null, done: false });

  const activeTenantId = activeTenant?.tenant_id || null;

  // ONE read per active workspace. Skipped entirely without a company claim: the
  // answer could only ever serve a company tab the user cannot open.
  useEffect(() => {
    if (!activeTenantId || options.length === 0) {
      setActiveRead({ tenantId: activeTenantId, customerId: null, done: true });
      return undefined;
    }
    let cancelled = false;
    setActiveRead({ tenantId: activeTenantId, customerId: null, done: false });
    // Started inside a promise chain so that a synchronous throw — or a stubbed
    // read that returns nothing — lands in `.catch`/unknown, never in a crash.
    Promise.resolve()
      .then(() => fetchCreditBalance({ tenant: activeTenantId }))
      .then(b => { if (!cancelled) setActiveRead({ tenantId: activeTenantId, customerId: b?.customer_id || null, done: true }); })
      .catch(() => { if (!cancelled) setActiveRead({ tenantId: activeTenantId, customerId: null, done: true }); });
    return () => { cancelled = true; };
  }, [activeTenantId, options.length]);

  // `?.` — defensive, like the layout's `adminTenants` read: some auth shapes omit it.
  const activeCompanyId = activeRead.tenantId === activeTenantId && isCustomerAdminOf?.(activeRead.customerId)
    ? activeRead.customerId
    : null;

  // ⭐ Known = the read for THIS active workspace has settled (success or failure).
  // Until then `activeCompanyId: null` means "not yet", never "another company".
  const activeCompanyResolved = activeRead.tenantId === activeTenantId && activeRead.done;

  // The default, with two or more claims: the active workspace's company when the
  // user administers it, else the first claim. (With exactly one claim it was
  // chosen synchronously above, without waiting for the read.)
  useEffect(() => {
    if (companyId || options.length === 0) return;
    if (!activeTenantId) { setCompanyId(options[0].customer_id); return; }
    if (activeRead.tenantId !== activeTenantId || !activeRead.done) return;
    setCompanyId(activeCompanyId || options[0].customer_id);
  }, [companyId, options, activeTenantId, activeRead, activeCompanyId]);

  const company = options.find(c => c.customer_id === companyId) || null;
  return { options, companyId: company ? companyId : null, company, setCompanyId, activeCompanyId, activeCompanyResolved };
}
